// Pure helpers for the Call History page (formatting, ranges, CSV).
import dayjs from 'dayjs';

export const LIMIT_OPTIONS = [50, 100, 250, 600, 1000];
export const DEFAULT_LIMIT = 600;
export const RANGE_PRESETS = ['day', 'week', 'month', 'custom'];

const TZ_LABELS = {
  'America/New_York': 'US/Eastern',
  'America/Chicago': 'US/Central',
  'America/Denver': 'US/Mountain',
  'America/Phoenix': 'US/Arizona',
  'America/Los_Angeles': 'US/Pacific',
  'America/Anchorage': 'US/Alaska',
  'Pacific/Honolulu': 'US/Hawaii',
};

/** @param {string|null|undefined} iana @returns {string} e.g. "US/Mountain", or "--" when unknown. */
export const tzLabel = (iana) => (iana ? (TZ_LABELS[iana] || iana) : '--');

/** @returns {string} "09-29-2026 10:52 am" */
export const fmtCallTime = (d) => (d && dayjs(d).isValid() ? dayjs(d).format('MM-DD-YYYY hh:mm a') : '--');

/** @returns {string} "9 seconds", "1 second", "2 min 5 sec" */
export function fmtCallDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} min ${r} sec` : `${m} min`;
}

/**
 * Local-calendar bounds for a preset, as ISO strings the API accepts.
 * @param {'day'|'week'|'month'} preset
 * @param {Date} [now]
 * @returns {{startDate: string, endDate: string}}
 */
export function presetRange(preset, now = new Date()) {
  const unit = { day: 'day', week: 'week', month: 'month' }[preset] || 'day';
  const base = dayjs(now);
  return { startDate: base.startOf(unit).toISOString(), endDate: base.endOf(unit).toISOString() };
}

/** @returns {string} <input type=date> value (local) for an ISO instant. */
export const toInputDate = (iso) => (iso ? dayjs(iso).format('YYYY-MM-DD') : '');

/** Local start/end of an <input type=date> value, as ISO. */
export const fromInputDate = (value, edge) => {
  if (!value) return '';
  const d = dayjs(value);
  return (edge === 'end' ? d.endOf('day') : d.startOf('day')).toISOString();
};

/** @returns {string} "Now viewing 9/1/26 - 10/31/26" */
export function viewingLabel(startDate, endDate) {
  const f = (v) => (v ? dayjs(v).format('M/D/YY') : '…');
  return `Now viewing ${f(startDate)} - ${f(endDate)}`;
}

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** @param {Array<object>} rows - Serialized call rows. @returns {string} CSV text. */
export function callsToCsv(rows) {
  const head = ['Lead', 'Phone', 'Direction', 'Time zone', 'Duration (sec)', 'Date/Time', 'Has recording'];
  const lines = rows.map((c) => [
    c.leadName || '', c.direction === 'inbound' ? c.fromNumber : c.toNumber, c.direction,
    tzLabel(c.leadTimezone), c.durationSec, fmtCallTime(c.createdAt), c.hasRecording ? 'yes' : 'no',
  ].map(csvCell).join(','));
  return [head.join(','), ...lines].join('\n');
}
